import {DestroyRef, inject, Injectable} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {TranslateService} from '@ngx-translate/core';
import {GlobalConfirmService} from 'src/app/shared/services/global-confirm/global-confirm.service';
import {VersionService} from 'src/app/shared/services/version/version.service';
import {Logger} from '@core/interfaces/Logger';
import {Observable, from, fromEvent, merge, of, timer} from 'rxjs';
import {auditTime, exhaustMap, filter, first, switchMap} from 'rxjs/operators';
import {BUTTON_COLOR, BUTTON_SIZE, BUTTON_TYPE} from '@discocil/fv-components';
import {MINUTE_IN_MS} from 'src/app/config/app.config';
import {environment} from '@env/environment';
import {Workbox} from 'workbox-window';

@Injectable({providedIn: 'root'})
export class PwaServiceWorkersService {
  private readonly logger = inject(Logger);
  private readonly confirmDialog = inject(GlobalConfirmService);
  private readonly translate = inject(TranslateService);
  private readonly versionService = inject(VersionService);
  private readonly destroyRef = inject(DestroyRef);

  private wb: Workbox | null = null;
  private isUpdateDialogOpened = false;

  constructor() {
    if (!('serviceWorker' in navigator) || !environment.isPWAEnabled) {
      return;
    }

    this.initWorkbox();
    this.initAutoUpdateCheck();
  }

  private initWorkbox(): void {
    this.wb = new Workbox('/sw.js');

    this.wb.addEventListener('installed', (event) => {
      this.logger.debug(`[PWA 🌎] Service Worker event: installed (isUpdate: ${!!event.isUpdate})`);

      if (!event.isUpdate) {
        this.logger.debug('[PWA 🌎] Initial installation. Syncing version from JSON...');
        this.versionService.ensureLocalFromJSON$().pipe(
          first()
          // eslint-disable-next-line rxjs-angular/prefer-takeuntil
        ).subscribe();
      }
    });

    this.wb.addEventListener('waiting', () => {
      this.logger.debug('[PWA 🌎] New content is available and waiting to be activated.');
      this.onVersionReady();
    });

    this.wb.register().catch(err => {
      this.logger.error(err, '[PWA 🌎] Registration failed');
    });
  }

  private initAutoUpdateCheck(): void {
    merge(
      timer(MINUTE_IN_MS, MINUTE_IN_MS * 5),
      fromEvent(window, 'focus'),
      fromEvent(window, 'online'),
      fromEvent(document, 'visibilitychange').pipe(filter(() => document.visibilityState === 'visible'))
    ).pipe(
      auditTime(2000),
      filter(() => navigator.onLine),
      exhaustMap(() => this.checkUpdate$()),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe();
  }

  private checkUpdate$(): Observable<void> {
    if (!this.wb) {
      return of(void 0);
    }

    this.logger.debug('[PWA 🌎] Checking for updates via Workbox hash check...');

    return from(this.wb.update()).pipe(
      switchMap(() => of(void 0))
    );
  }

  private onVersionReady(): void {
    if (this.isUpdateDialogOpened) {
      return;
    }

    this.isUpdateDialogOpened = true;
    this.requestConfirm$()
      .pipe(
        switchMap(() => {
          if (this.wb) {
            this.wb.messageSkipWaiting();
          }

          return of(void 0);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        complete: () => {
          this.isUpdateDialogOpened = false;
          setTimeout(() => location.reload(), 200);
        }
      });
  }

  private requestConfirm$(): Observable<void> {
    const keys = [
      'pwa.confirm_modal.new_update_message',
      'pwa.confirm_modal.new_update_submessage',
      'pwa.confirm_modal.new_update.reset_button'
    ];

    return this.translate.get(keys).pipe(
      first(),
      switchMap(texts => new Observable<void>(observer => {
        this.confirmDialog.open({
          noClose: true,
          icon: 'fas fa-shield-check',
          iconSize: 'text-3xl',
          hideCloseIcon: true,
          message: texts[keys[0]],
          submessage: texts[keys[1]],
          buttons: [{
            text: texts[keys[2]],
            color: BUTTON_COLOR.primary,
            size: BUTTON_SIZE.full,
            type: BUTTON_TYPE.filled,
            preventAutoClose: true,
            handler: (): void => {
              observer.next();
              observer.complete();
            }
          }]
        });
      }))
    );
  }
}
